import { useCallback, useEffect } from "react";
import { useMutation, useQuery } from "urql";
import { graphql } from "../../../__generated__/gql";
import Select from "../../../ui/Select/Select";
import Page from "../../Page";
import urls from "../../../routes";
import styles from "./Webflow.module.scss";
import Card from "../../../ui/Card/Card";
import SettingsSection from "../../Settings/components/SettingsSection";
import FieldsTable from "./FieldsTable";
import fieldMappingSchema from "./schema";
import Header from "../../Settings/components/Header/Header";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  SupportedMacawField,
  WebflowFieldMapping,
} from "../../../__generated__/gql/graphql";
import Loader from "../../../ui/Loader/Loader";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
// import AuthorizeWebflow from "./AuthorizeWebflow";
import Badge from "../../../ui/Badge/Badge";
import { Webflow as WebflowLogo } from "../../../ui/LogoIcon/LogoIcon";
import Button from "../../../ui/Button/Button";
import { useNavigate } from "react-router-dom";
import Feedback from "../../../ui/Feedback/Feedback";
import { useToast } from "../../../ui/Toast/ToastContext";
import TextInput from "../../../ui/TextInput/TextInput";
import Modal from "../../../ui/Modal/Modal";
import Label from "../../../ui/Label/Label";
import ConfirmationModal from "../../../ui/Modal/ConfirmationModal";
import webflowFieldTypeSchema from "./webflow-field-type-schema";

const WebflowCollectionsQuery = graphql(/* GraphQL */ `
  query webflowCollections($siteId: String!) {
    webflowCollections(siteId: $siteId) {
      id
      name
    }
  }
`);

const WebflowCollectionFieldsQuery = graphql(/* GraphQL */ `
  query webflowCollectionFields($collectionId: String!) {
    webflowCollectionFields(collectionId: $collectionId) {
      id
      name
      displayName
      type
      isEditable
      isRequired
      options {
        value
        label
      }
    }
  }
`);

const WebflowConnectionQuery = graphql(/* GraphQL */ `
  query webflowConnection {
    webflowConnection {
      id
      connected
      sites {
        id
        name
      }
      settings {
        siteId
        collectionId
        fieldMapping {
          webflowField {
            id
            isEditable
            isRequired
            name
            displayName
            type
            options {
              value
              label
            }
          }
          macawField
          defaultValue
        }
      }
    }
  }
`);

const UpdateWebflowConnectionSettingsMutation = graphql(/* GraphQL */ `
  mutation updateWebflowConnectionSettings(
    $input: UpdateWebflowConnectionSettingsInput!
  ) {
    updateWebflowConnectionSettings(input: $input) {
      id
      connected
      settings {
        siteId
        collectionId
        fieldMapping {
          webflowField {
            id
            isEditable
            isRequired
            name
            displayName
            type
            options {
              value
              label
            }
          }
          macawField
          defaultValue
        }
      }
    }
  }
`);

const SetWebflowConnectionAccessTokenMutation = graphql(/* GraphQL */ `
  mutation setWebflowConnectionAccessToken($accessToken: String!) {
    setupWebflowConnection(input: { accessToken: $accessToken }) {
      id
    }
  }
`);

const DeleteWebflowConnectionMutation = graphql(/* GraphQL */ `
  mutation deleteWebflowConnection {
    deleteWebflowConnection {
      id
    }
  }
`);

const schema = z.object({
  site: z.string().min(1, "Required"),
  collection: z.string().min(1, "Required"),
  fields: fieldMappingSchema,
});

const isFieldMapping = (
  field: WebflowFieldMapping,
): field is WebflowFormData["fields"][number] =>
  !!webflowFieldTypeSchema.safeParse(field.webflowField) &&
  (!field.macawField ||
    !!Object.values(SupportedMacawField).find(
      (value) => value === field.macawField,
    )) &&
  field.webflowField.name !== "slug";

type WebflowFormData = z.infer<typeof schema>;

function WebflowForm({
  connected,
  sites,
  defaultValues,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  refetch,
}: {
  connected: boolean;
  sites: { value: string; label: string }[];
  defaultValues?: WebflowFormData;
  refetch: () => void;
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const methods = useForm<WebflowFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const {
    watch,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting, isValid },
  } = methods;

  const [site, collection, fields] = watch(["site", "collection", "fields"]);

  const [webflowCollectionsResponse] = useQuery({
    query: WebflowCollectionsQuery,
    pause: !site,
    variables: {
      siteId: site,
    },
  });
  const [webflowCollectionFieldsResponse, refetchWebflowCollectionFields] =
    useQuery({
      query: WebflowCollectionFieldsQuery,
      pause: !!defaultValues?.collection || !collection,
      variables: { collectionId: collection },
    });

  const [, updateWebflowConnectionSettings] = useMutation(
    UpdateWebflowConnectionSettingsMutation,
  );
  const [, deleteWebflowConnection] = useMutation(
    DeleteWebflowConnectionMutation,
  );

  const onSubmit = useCallback(
    async (formData: WebflowFormData) => {
      const fieldMapping = formData.fields?.map((field) => ({
        webflowField: {
          ...field.webflowField,
          options: undefined,
        },
        macawField: Object.values(SupportedMacawField).find(
          (value) => value === field.macawField,
        ),
        defaultValue: field.defaultValue,
        isRequired: field.webflowField.isRequired,
      }));

      await updateWebflowConnectionSettings({
        input: {
          siteId: formData.site,
          collectionId: formData.collection,
          fieldMapping,
        },
      });

      if (!connected) {
        showToast({
          title: "Webflow connected",
          variant: "success",
        });
      }

      reset(undefined, { keepValues: true });
    },
    [connected, reset, showToast, updateWebflowConnectionSettings],
  );

  useEffect(() => {
    if (collection) {
      if (collection === defaultValues?.collection) {
        setValue("fields", defaultValues.fields);
      } else {
        refetchWebflowCollectionFields({ requestPolicy: "network-only" });
      }
    }
  }, [collection, defaultValues, refetchWebflowCollectionFields, setValue]);

  useEffect(() => {
    if (webflowCollectionFieldsResponse.data) {
      const fields = getValues("fields");
      const webflowFields =
        webflowCollectionFieldsResponse.data?.webflowCollectionFields;

      const newFields = webflowFields
        ?.filter(
          (field): field is WebflowFormData["fields"][number]["webflowField"] =>
            webflowFieldTypeSchema.safeParse(field.type).success &&
            field.name !== "slug",
        )
        .map((field) => {
          const existingField = fields?.find(
            (f) => f.webflowField.id === field.id,
          );

          return {
            webflowField: field,
            macawField: existingField?.macawField,
            defaultValue: existingField?.defaultValue,
          };
        });

      setValue("fields", newFields);
    }
  }, [getValues, setValue, webflowCollectionFieldsResponse.data]);

  const collections =
    webflowCollectionsResponse.data?.webflowCollections?.map((collection) => ({
      value: collection.id,
      label: collection.name,
    })) ?? [];

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Header
              title="Webflow"
              titleElement={
                connected ? (
                  <Badge color="success">Connected</Badge>
                ) : (
                  <Badge color="default">Not Connected</Badge>
                )
              }
              description={
                <>
                  Setup publishing to Webflow. For help, you can check out our{" "}
                  <a
                    href="https://docs.macawhq.com/en/articles/9911805-setting-up-webflow-connection"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Webflow setup guide.
                  </a>
                </>
              }
              saveButtonText={connected ? "Save" : "Connect"}
              isSubmitting={isSubmitting}
              isDirty={isDirty}
              isDisabled={!isValid}
              onCancel={() => reset()}
            />

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                marginBottom: 20,
                paddingTop: 24,
                borderTop: "1px solid var(--color-gray-200)",
              }}
            >
              <Badge size="large" variant="badge" showDot={false}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <WebflowLogo size="medium" />
                  <span
                    style={{ fontWeight: 600, color: "var(--color-gray-800)" }}
                  >
                    Webflow connection
                  </span>
                </div>
              </Badge>
              <Button
                variant="link"
                onClick={async () => {
                  await deleteWebflowConnection({});
                  await navigate(urls.publishing.connections.root);
                }}
              >
                Disconnect
              </Button>
            </div>
            <SettingsSection label="Site" description="Your Webflow site">
              <div className={styles.select}>
                <Controller
                  name="site"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        data={sites}
                        value={field.value}
                        onChange={(e) => {
                          setValue("collection", "");
                          setValue("fields", []);
                          field.onChange(e);
                        }}
                      />
                      {error?.message && (
                        <Feedback className={styles.feedback}>
                          {error.message}
                        </Feedback>
                      )}
                    </>
                  )}
                />
              </div>
            </SettingsSection>
            {site && (
              <>
                <SettingsSection
                  label="Articles collection"
                  description="Your Webflow articles collection"
                >
                  <div className={styles.select}>
                    {webflowCollectionsResponse.fetching ? (
                      <Loader size="small" />
                    ) : (
                      <Controller
                        name="collection"
                        control={methods.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Select
                              withinPortal
                              disabled={collections.length === 0}
                              placeholder={
                                collections.length === 0
                                  ? "No collections found for site"
                                  : undefined
                              }
                              data={collections}
                              value={field.value}
                              onChange={field.onChange}
                            />
                            {error?.message && (
                              <Feedback className={styles.feedback}>
                                {error.message}
                              </Feedback>
                            )}
                          </>
                        )}
                      />
                    )}
                  </div>
                </SettingsSection>

                {collection && (
                  <SettingsSection
                    label="Map fields"
                    description={
                      <>
                        <p>
                          Map Macaw fields to your Webflow collections fields.
                        </p>
                        {/* <Button
                          className={styles.refreshButton}
                          variant="link"
                          onClick={refetch}
                        >
                          Refresh fields
                        </Button> */}
                      </>
                    }
                  >
                    <FieldsTable
                      data={fields ?? []}
                      // loading={webflowFieldsResponse.fetching}
                    />
                  </SettingsSection>
                )}
              </>
            )}
          </form>
        </FormProvider>
      </Card>
    </div>
  );
}

function WebflowAPITokenModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: { accessToken: string }) => void;
}) {
  const [, setWebflowConnectionAccessToken] = useMutation(
    SetWebflowConnectionAccessTokenMutation,
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty, isLoading },
  } = useForm<{ accessToken: string }>();

  const submit = async (data: { accessToken: string }) => {
    await setWebflowConnectionAccessToken({ accessToken: data.accessToken });
    onSubmit(data);
  };

  return (
    <Modal isOpen onClose={onClose} title="Setup Webflow connection">
      <form onSubmit={handleSubmit(submit)} className={styles.form}>
        <ConfirmationModal
          type="submit"
          onClose={onClose}
          loading={isLoading}
          confirmDisabled={!isDirty}
        >
          <Label
            label="Enter your Webflow API Key"
            tooltip={
              <div>
                <p>API key must have the following permissions:</p>
                <ul>
                  <li>CMS: Read and write</li>
                  <li>Site: Read</li>
                </ul>
              </div>
            }
          >
            <Controller
              name="accessToken"
              control={control}
              render={({ field }) => (
                <TextInput {...field} placeholder="API key" />
              )}
            />
          </Label>
        </ConfirmationModal>
      </form>
    </Modal>
  );
}

function Webflow() {
  const navigate = useNavigate();

  const [webflowConnectionResponse, refetch] = useQuery({
    query: WebflowConnectionQuery,
  });

  const loading = webflowConnectionResponse.fetching;

  const sites =
    webflowConnectionResponse.data?.webflowConnection?.sites?.map((site) => ({
      value: site.id,
      label: site.name,
    })) ?? [];

  const settings = webflowConnectionResponse.data?.webflowConnection?.settings;

  return (
    <Page
      title="Webflow"
      documentTitle="Webflow setup"
      loading={loading}
      // loading={loading || !webflowConnectionResponse.data?.webflowConnection}
      breadcrumbs={[
        { url: urls.publishing.connections.root, name: "Publishing" },
      ]}
    >
      {!loading &&
        (!webflowConnectionResponse.data?.webflowConnection ? (
          // <AuthorizeWebflow />
          <WebflowAPITokenModal
            onClose={() => navigate(urls.publishing.root)}
            onSubmit={() => refetch({ requestPolicy: "network-only" })}
          />
        ) : (
          <WebflowForm
            refetch={() =>
              refetch({
                requestPolicy: "network-only",
              })
            }
            connected={
              !!webflowConnectionResponse.data.webflowConnection.connected
            }
            defaultValues={{
              site: settings?.siteId ?? "",
              collection: settings?.collectionId ?? "",
              fields: settings?.fieldMapping?.filter(isFieldMapping) ?? [],
            }}
            sites={sites}
          />
        ))}
    </Page>
  );
}

export default Webflow;
